import React from "react";
import classnames from "classnames";
import { Classes, Dialog, DialogProps } from "@blueprintjs/core";

/**
 * Модальное окно в стиле planr: Blueprint `Dialog` с классом `figma-dialog`
 * и настройками, которые до сих пор повторялись в каждом окне вручную:
 * затемнённая подложка `standard`, крестик в шапке, окно не закрывается
 * кликом мимо, чтобы не потерять введённое.
 *
 * Последнее — единственное умолчание, которое расходится с Blueprint: у 65 окон
 * из 123 `canOutsideClickClose` не задан, и сейчас они закрываются кликом мимо.
 * Переводя такое окно, решайте осознанно; сохранить прежнее поведение —
 * `canOutsideClickClose={true}`. Остальное совпадает с Blueprint, в том числе
 * `enforceFocus` (его отключают лишь единичные окна).
 *
 * Любую настройку можно переопределить пропом — он пробрасывается в `Dialog`.
 *
 * Тело и подвал — `DialogBody` и `DialogFooter`. Это отдельные компоненты, а не
 * слоты окна, чтобы их можно было положить внутрь `<Form>` Formik-а: кнопка
 * сохранения в подвале должна оказаться внутри формы.
 *
 * ```tsx
 * <PlanrDialog isOpen={isOpen} onClose={close} title="Новый отдел" width={500}>
 *     <Form>
 *         <DialogBody>…поля…</DialogBody>
 *         <DialogFooter>
 *             <StandardFormButtons {...formProps} … />
 *         </DialogFooter>
 *     </Form>
 * </PlanrDialog>
 * ```
 */
export const PlanrDialog = ({ variant = "figma", width, className, style, ...props }: PlanrDialogProps) => (
    <Dialog
        backdropClassName="standard"
        isCloseButtonShown={true}
        canOutsideClickClose={false}
        {...props}
        className={classnames({ "figma-dialog": variant === "figma" }, className)}
        style={typeof width === "undefined" ? style : { width, ...style }}
    />
);

export interface PlanrDialogProps extends DialogProps {
    /** `figma` — фирменная шапка 80px и поля 30px; `plain` — метрики обычного окна Blueprint. */
    variant?: "figma" | "plain";
    /** Ширина окна; число — в пикселях. Для сложной вёрстки размер лучше задать CSS-модулем. */
    width?: number | string;
}

export const DialogBody = ({ className, children, ...rest }: DialogSectionProps) => (
    <div {...rest} className={classnames(Classes.DIALOG_BODY, className)}>
        {children}
    </div>
);

/** Подвал окна: кнопки прижаты вправо. */
export const DialogFooter = ({ className, children, ...rest }: DialogSectionProps) => (
    <div {...rest} className={classnames(Classes.DIALOG_FOOTER, className)}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>{children}</div>
    </div>
);

interface DialogSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
}
